import { useParams } from 'react-router-dom'
import { useProcotol } from '@services/protocols/protocols.hook'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Stack,
  Alert
} from '@mui/material'
import { DeleteForever } from '@mui/icons-material'
import { usePersistedFormik } from '../hooks/usePersistedFormik'
import * as Yup from 'yup'
import { PROTOCOL_TYPE } from '@services/protocols/protocols.consts'
import { getProtocols } from '@services/protocols/protocols.helpers'
import AutoSaveIndicator, { AutoSaveStyles } from './AutoSaveIndicator'
import {
  useFlatHouseholdAppliances,
  useProtocolHouseholdAppliances,
  useWarehouseHouseholdAppliances,
  useHouseholdApplianceTypes,
  useResidentialHouseholdApplianceProducers
} from '@services/protocols/hooks/protocolHouseholdAppliances'
import { useMemo } from 'react'
import HouseholdApplianceTypePreview from './HouseholdApplianceTypePreview'

function ProtocolHouseholdAppliances({ isReadOnly }: { isReadOnly: boolean }) {
  const { id } = useParams<{ id: string }>()
  const { protocol } = useProcotol(Number(id))
  const { currentProtocol, oppositeProtocol } = getProtocols(protocol)

  const {
    protocolHouseholdAppliances,
    createProtocolHouseholdAppliance: createProtocolHouseholdApplianceCurrent,
    deleteProtocolHouseholdAppliance: deleteProtocolHouseholdApplianceCurrent
  } = useProtocolHouseholdAppliances(Number(currentProtocol?.id))
  const { flatHouseholdAppliances } = useFlatHouseholdAppliances(Number(currentProtocol?.id))
  const { warehouseHouseholdAppliances } = useWarehouseHouseholdAppliances(
    Number(currentProtocol?.id)
  )

  const { householdApplianceTypes, getHouseholdApplianceNameById } = useHouseholdApplianceTypes()
  const { residentialHouseholdApplianceProducers, getHouseholdApplianceProducerNameById } =
    useResidentialHouseholdApplianceProducers()

  const { protocolHouseholdAppliances: oppositeProtocolHouseholdAppliances } =
    useProtocolHouseholdAppliances(Number(oppositeProtocol?.id))

  const householdApplianceCreateform = usePersistedFormik(
    {
      validateOnMount: false,
      validateOnChange: false,
      initialValues: {
        householdAppliance: null
      },
      validationSchema: Yup.object().shape({
        householdAppliance: Yup.number().required('Wybierz sprzęt AGD')
      }),
      onSubmit: async (values: { householdAppliance: number | null }) => {
        await createProtocolHouseholdApplianceCurrent.mutateAsync({
          householdAppliance: values.householdAppliance as number
        })
        householdApplianceCreateform.resetForm()
      }
    },
    {
      formType: 'household-appliances',
      protocolId: Number(currentProtocol?.id || id),
      autoSaveInterval: 3000,
      showNotifications: false, // Less intrusive for furniture forms
      clearOnSubmit: true
    }
  )

  const removeProtocolHouseholdAppliance = async (applianceId: number) => {
    householdApplianceCreateform.setSubmitting(true)
    await deleteProtocolHouseholdApplianceCurrent.mutateAsync(applianceId)
    householdApplianceCreateform.setSubmitting(false)
  }

  const householdApplianceList = useMemo(() => {
    return [
      ...flatHouseholdAppliances.map((item) => ({
        key: 'flat-' + item.id,
        value: item.id,
        label: !!item.producer
          ? `${getHouseholdApplianceProducerNameById(item.producer)} - ${getHouseholdApplianceNameById(item.type)} - ${item.serialNumber}`
          : `${getHouseholdApplianceNameById(item.type)} - ${item.serialNumber}`
      })),
      ...warehouseHouseholdAppliances.map((item) => ({
        key: 'warehouse-' + item.id,
        value: item.id,
        label: !!item.producer
          ? `[Magazyn] ${getHouseholdApplianceProducerNameById(item.producer)} - ${getHouseholdApplianceNameById(item.type)} - ${item.serialNumber}`
          : `[Magazyn] ${getHouseholdApplianceNameById(item.type)} - ${item.serialNumber}`
      }))
    ]
  }, [
    flatHouseholdAppliances,
    warehouseHouseholdAppliances,
    householdApplianceTypes,
    residentialHouseholdApplianceProducers
  ])

  return (
    <Box>
      {/* Unsaved changes indicator */}
      {householdApplianceCreateform.hasUnsavedChanges && (
        <Alert
          severity='warning'
          sx={{ mb: 2 }}
          action={
            <Button
              size='small'
              onClick={householdApplianceCreateform.restoreFromSaved}
              sx={{ mr: 1 }}
            >
              Odrzuć zmiany
            </Button>
          }
        >
          Formularz zawiera niezapisane zmiany
        </Alert>
      )}

      {protocolHouseholdAppliances.length === 0 &&
      oppositeProtocolHouseholdAppliances.length === 0 ? (
        <Typography variant='caption' fontWeight={700} display='block' mb={4}>
          Brak sprzętów AGD
        </Typography>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: '100%' }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Typ</TableCell>
                <TableCell align='right'>Numer seryjny</TableCell>
                {!isReadOnly && <TableCell align='right'></TableCell>}
              </TableRow>
            </TableHead>
            {currentProtocol?.protocolType === PROTOCOL_TYPE.acceptance ? (
              <TableBody>
                {protocolHouseholdAppliances.map((householdAppliance) => (
                  <TableRow
                    key={householdAppliance.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component='th' scope='row'>
                      {householdAppliance.householdApplianceType}
                    </TableCell>
                    <TableCell align='right'> {householdAppliance.serialNumber}</TableCell>
                    {!isReadOnly && (
                      <TableCell align='right'>
                        <IconButton
                          disabled={householdApplianceCreateform.isSubmitting}
                          onClick={() => removeProtocolHouseholdAppliance(householdAppliance.id)}
                        >
                          <DeleteForever />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                <>
                  {oppositeProtocolHouseholdAppliances.map((householdAppliance) => (
                    <TableRow
                      key={householdAppliance.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component='th' scope='row'>
                        {householdAppliance.householdApplianceType}
                      </TableCell>
                      <TableCell align='right'> {householdAppliance.serialNumber}</TableCell>
                      {!isReadOnly && (
                        <TableCell align='right'>
                          <IconButton
                            disabled={householdApplianceCreateform.isSubmitting}
                            onClick={() => removeProtocolHouseholdAppliance(householdAppliance.id)}
                          ></IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {protocolHouseholdAppliances.map((protocolHouseholdAppliances) => (
                    <TableRow
                      key={protocolHouseholdAppliances.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component='th' scope='row'>
                        {protocolHouseholdAppliances.householdApplianceType}
                      </TableCell>
                      <TableCell align='right'>
                        {protocolHouseholdAppliances.serialNumber}
                      </TableCell>
                      {!isReadOnly && (
                        <TableCell align='right'>
                          <IconButton
                            disabled={householdApplianceCreateform.isSubmitting}
                            onClick={() =>
                              removeProtocolHouseholdAppliance(protocolHouseholdAppliances.id)
                            }
                          >
                            <DeleteForever />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      )}

      {!isReadOnly && (
        <>
          <Typography variant='caption' fontWeight={700}>
            Dodaj sprzętów AGD
          </Typography>
          <form onSubmit={householdApplianceCreateform.handleSubmit}>
            <FormControl
              fullWidth
              sx={{ my: 2 }}
              error={!!householdApplianceCreateform.errors.householdAppliance}
            >
              <InputLabel id='householdAppliance'>Sprzętu AGD</InputLabel>
              <Select
                disabled={householdApplianceCreateform.isSubmitting}
                labelId='householdAppliance'
                value={householdApplianceCreateform.values.householdAppliance}
                label='householdAppliance'
                onChange={({ target }) =>
                  householdApplianceCreateform.setFieldValue('householdAppliance', target.value)
                }
              >
                {householdApplianceList.map((householdAppliance) => (
                  <MenuItem
                    key={householdAppliance.key}
                    value={householdAppliance.value}
                    disabled={protocolHouseholdAppliances.some(
                      (pHA) => pHA.householdAppliance === householdAppliance.value
                    )}
                  >
                    {householdAppliance.label}
                  </MenuItem>
                ))}
              </Select>
              {!!householdApplianceCreateform.errors.householdAppliance && (
                <FormHelperText>
                  {String(householdApplianceCreateform.errors.householdAppliance)}
                </FormHelperText>
              )}
              {!!householdApplianceCreateform.values.householdAppliance && (
                <Box sx={{ mt: 2 }}>
                  <HouseholdApplianceTypePreview
                    id={householdApplianceCreateform.values.householdAppliance}
                    protocolId={Number(currentProtocol?.id)}
                  />
                </Box>
              )}
            </FormControl>
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ mt: 2 }}
            >
              <AutoSaveIndicator
                isDirty={
                  householdApplianceCreateform.dirty &&
                  !householdApplianceCreateform.hasUnsavedChanges
                }
                isAutoSaving={false}
                lastSavedTime={householdApplianceCreateform.lastSaved}
              />
              <Button
                type='submit'
                variant='contained'
                loading={householdApplianceCreateform.isSubmitting}
              >
                Dodaj
              </Button>
            </Stack>
          </form>
        </>
      )}

      <style>{AutoSaveStyles}</style>
    </Box>
  )
}

export default ProtocolHouseholdAppliances
